import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getPublicSolutions, type SolutionDto } from '@/api/solution';
import { fetchDictBatch } from '@/api/dict';
import { getPublicCms } from '@/api/cms';

function readText(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function clampStars(value: unknown): number {
  if (typeof value !== 'number' || Number.isNaN(value)) return 0;
  return Math.max(0, Math.min(5, Math.round(value)));
}

interface SolutionsSectionContent {
  tag: string;
  title: string;
  subtitle: string;
  btnText: string;
}

const EMPTY_SOL: SolutionsSectionContent = {
  tag: '',
  title: '',
  subtitle: '',
  btnText: '',
};

function normalizeSolutionsContent(raw: unknown): SolutionsSectionContent {
  if (!raw || typeof raw !== 'object') return EMPTY_SOL;
  const parsed = raw as Record<string, unknown>;
  return {
    tag: readText(parsed.tag),
    title: readText(parsed.title),
    subtitle: readText(parsed.subtitle),
    btnText: readText(parsed.btnText),
  };
}

export default function SolutionsSection() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [content, setContent] = useState<SolutionsSectionContent>(EMPTY_SOL);
  const [contentError, setContentError] = useState<string | null>(null);
  const [solutions, setSolutions] = useState<SolutionDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dictCategories, setDictCategories] = useState<{ label: string; value: string }[]>([]);

  const refreshContent = useCallback(async () => {
    try {
      const cms = await getPublicCms('home_page_content');
      setContent(normalizeSolutionsContent(cms?.solutions));
      setContentError(null);
    } catch (err) {
      setContent(EMPTY_SOL);
      setContentError(err instanceof Error ? err.message : '首页方案区内容加载失败');
    }
  }, []);

  useEffect(() => {
    fetchDictBatch(['solution_category'])
      .then((dicts) => {
        if (dicts['solution_category']) {
          setDictCategories(dicts['solution_category'].map((x) => ({ label: x.label, value: x.value })));
        }
      })
      .catch((err) => console.error('Failed to load solution categories in homepage section:', err));
  }, []);

  useEffect(() => {
    void refreshContent();
    const handleUpdate = () => void refreshContent();
    window.addEventListener('wanxiang_page_content_updated', handleUpdate);
    return () => window.removeEventListener('wanxiang_page_content_updated', handleUpdate);
  }, [refreshContent]);

  const getCategoryLabel = useCallback((cat: string) => {
    if (cat === '全部') return '全部';
    const found = dictCategories.find((c) => c.value === cat);
    return found ? found.label : cat;
  }, [dictCategories]);

  const refreshSolutions = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const res = await getPublicSolutions();
      setSolutions(Array.isArray(res?.list) ? res.list : []);
    } catch (err) {
      setSolutions([]);
      setLoadError(err instanceof Error ? err.message : '获取方案失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshSolutions();
    const handleCustom = () => void refreshSolutions();
    window.addEventListener('wanxiang_solutions_updated', handleCustom);
    return () => {
      window.removeEventListener('wanxiang_solutions_updated', handleCustom);
    };
  }, [refreshSolutions]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(
      solutions
        .map((item) => readText(item.category))
        .filter(Boolean),
    ));
    return ['全部', ...cats];
  }, [solutions]);

  const sortedSolutions = useMemo(() => {
    return [...solutions].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return Number((a as any).sortOrder ?? 999) - Number((b as any).sortOrder ?? 999);
    });
  }, [solutions]);

  const filtered = useMemo(() => {
    if (activeCategory === '全部') {
      return sortedSolutions.slice(0, 6);
    }
    return sortedSolutions.filter((s) => readText(s.category) === activeCategory).slice(0, 6);
  }, [activeCategory, sortedSolutions]);

  return (
    <section id="solutions" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium mb-4">
            <i className="ri-lightbulb-line"></i>
            {content.tag || '无'}
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{content.title || '无'}</h2>
          <p className="text-gray-500 max-w-xl mx-auto">{content.subtitle || '无'}</p>
          {contentError && (
            <p className="text-xs text-red-500 mt-3">区块内容加载失败：{contentError}</p>
          )}
        </div>

        {categories.length > 1 && (
          <div className="flex flex-wrap items-center gap-2 justify-center mb-6 px-2">
            <span className="flex items-center gap-1 text-xs font-semibold text-gray-400">
              <i className="ri-fire-line text-[#f97316]"></i>热门：
            </span>
            {categories.filter((c) => c !== '全部').map((cat, idx) => {
              const count = solutions.filter((s) => readText(s.category) === cat).length;
              const tagColors = ['#0ea5e9', '#10b981', '#f97316', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444'];
              const color = tagColors[idx % tagColors.length];
              return (
                <button
                  key={cat}
                  onClick={() => navigate(`/solutions?category=${encodeURIComponent(cat)}`)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap hover:opacity-90 hover:scale-105"
                  style={{ backgroundColor: `${color}12`, border: `1px solid ${color}30`, color }}
                >
                  {getCategoryLabel(cat)}
                  <span className="text-xs opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? 'bg-[#0ea5e9] text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {getCategoryLabel(cat)}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="bg-[#f8fafc] border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-500">
            正在加载方案...
          </div>
        ) : loadError ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-sm text-red-500">
            方案数据加载失败：{loadError}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#f8fafc] border border-gray-100 rounded-2xl p-8 text-center text-sm text-gray-400">
            暂无可展示方案
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((solution) => {
              const id = String(solution.id ?? '');
              const title = readText(solution.title) || '无';
              const description = readText(solution.description) || '无';
              const category = readText(solution.category) || '无';
              const commercial = readText(solution.commercial) || '无';
              const opensource = readText(solution.opensource) || '无';
              const costSaving = readText(solution.costSaving) || '无';
              const image = readText(solution.image);
              const tags = (solution.tags ?? []).map((tag) => readText(tag)).filter(Boolean);
              const stars = clampStars(solution.stars);
              return (
                <div
                  key={id}
                  className="group bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#0ea5e9]/30 transition-all duration-300"
                >
                  <div className="relative h-44 overflow-hidden">
                    {image ? (
                      <img
                        src={image}
                        alt={title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-slate-300 to-slate-500 flex items-center justify-center text-white/90 text-xs">
                        暂无封面
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                    <div className="absolute top-3 left-3 flex gap-1.5">
                      <span className="px-2 py-1 bg-[#0ea5e9] text-white text-xs font-medium rounded-md">
                        {getCategoryLabel(category)}
                      </span>
                      {solution.pinned && (
                        <span className="px-2 py-1 bg-gradient-to-r from-[#f97316] to-[#ef4444] text-white text-xs font-bold rounded-md flex items-center gap-0.5">
                          <i className="ri-fire-fill"></i>热门
                        </span>
                      )}
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 bg-[#f97316] text-white text-xs font-medium rounded-md">
                        省 {costSaving}
                      </span>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 text-base mb-1">{title}</h3>
                    <p className="text-gray-500 text-sm mb-3 line-clamp-2">{description}</p>

                    <div className="flex items-center gap-2 mb-3 text-xs">
                      <span className="text-gray-400">商业方案:</span>
                      <span className="text-gray-600 font-medium">{commercial}</span>
                      <i className="ri-arrow-right-line text-[#0ea5e9]"></i>
                      <span className="text-[#0ea5e9] font-medium">{opensource}</span>
                    </div>

                    {tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {tags.map((tag) => (
                          <span key={tag} className="px-2 py-0.5 bg-gray-50 text-gray-500 text-xs rounded-md">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between">
                      {stars > 0 ? (
                        <div className="flex gap-0.5">
                          {[...Array(5)].map((_, i) => (
                            <i
                              key={i}
                              className={`ri-star-fill text-sm ${i < stars ? 'text-[#f97316]' : 'text-gray-200'}`}
                            ></i>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">无评分</span>
                      )}
                      <Link
                        to={`/solutions/${id}`}
                        className="px-4 py-1.5 bg-[#0ea5e9]/10 text-[#0ea5e9] text-sm font-medium rounded-lg hover:bg-[#0ea5e9] hover:text-white transition-all cursor-pointer whitespace-nowrap"
                      >
                        查看方案
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/solutions"
            className="inline-flex items-center gap-2 px-8 py-3 border-2 border-[#0ea5e9] text-[#0ea5e9] font-semibold rounded-xl hover:bg-[#0ea5e9] hover:text-white transition-all cursor-pointer whitespace-nowrap"
          >
            {content.btnText || '无'}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
